import { useState, useEffect, useRef } from 'react'
import ProfileQuickBubble from './ProfileQuickBubble'

/**
 * ProfileDock - Bottom-left profile widget with notification badge
 *
 * Shows:
 * - User avatar with notification badge
 * - Display name
 * - Presence indicator (green dot)
 *
 * Clicking opens ProfileQuickBubble above it
 */
export default function ProfileDock({
  profile,
  notifications = [],
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  onOpenFullProfile,
  onOpenSettings,
  onUpdateStatus,
}) {
  const [isBubbleOpen, setIsBubbleOpen] = useState(false)
  const [isPulsing, setIsPulsing] = useState(false)
  const dockRef = useRef(null)
  const prevCountRef = useRef(unreadCount)

  // Pulse animation when new notifications arrive
  useEffect(() => {
    if (unreadCount > prevCountRef.current) {
      setIsPulsing(true)
      const timer = setTimeout(() => setIsPulsing(false), 1000)
      return () => clearTimeout(timer)
    }
    prevCountRef.current = unreadCount
  }, [unreadCount])

  // Close bubble on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isBubbleOpen) {
        setIsBubbleOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isBubbleOpen])

  // Return focus to dock when bubble closes
  useEffect(() => {
    if (!isBubbleOpen && dockRef.current) {
      // Small delay to ensure transition completes
      const timer = setTimeout(() => {
        dockRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isBubbleOpen])

  const handleDockClick = () => {
    setIsBubbleOpen(!isBubbleOpen)
  }

  const handleCloseBubble = () => {
    setIsBubbleOpen(false)
  }

  const handleOpenFullProfile = () => {
    setIsBubbleOpen(false)
    onOpenFullProfile?.()
  }

  // Format badge text
  const getBadgeText = () => {
    if (unreadCount === 0) return null
    if (unreadCount > 9) return '9+'
    return unreadCount.toString()
  }

  // Get status color
  const getStatusColor = () => {
    switch (profile.status) {
      case 'online': return 'bg-gjyl-success'
      case 'idle': return 'bg-gjyl-warning'
      case 'dnd': return 'bg-gjyl-danger'
      default: return 'bg-gjyl-slate-600'
    }
  }

  return (
    <div className="relative">
      {/* Profile Dock Button */}
      <button
        ref={dockRef}
        onClick={handleDockClick}
        className={`
          relative group
          w-11 h-11 rounded-gjyl
          overflow-visible
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-gjyl-indigo-500 focus:ring-offset-2 focus:ring-offset-gjyl-darker
          ${isBubbleOpen ? 'ring-2 ring-gjyl-indigo-500' : ''}
        `}
        aria-label={`Your profile${unreadCount > 0 ? ` (${unreadCount} unread notifications)` : ''}`}
        aria-expanded={isBubbleOpen}
        aria-haspopup="dialog"
      >
        {/* Avatar Container */}
        <div className={`
          w-full h-full rounded-gjyl overflow-hidden
          border-2 transition-colors duration-200
          ${isBubbleOpen
            ? 'border-gjyl-indigo-500'
            : 'border-gjyl-slate-700 group-hover:border-gjyl-indigo-500/50'
          }
        `}>
          {profile.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={profile.username}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gjyl-slate-700 flex items-center justify-center text-white font-semibold text-xs">
              {profile.username?.substring(0, 2).toUpperCase() || 'U'}
            </div>
          )}
        </div>

        {/* Presence Status Indicator */}
        <div
          className={`
            absolute -bottom-0.5 -right-0.5
            w-3.5 h-3.5 rounded-full
            border-2 border-gjyl-darker
            ${getStatusColor()}
          `}
          title={profile.status || 'offline'}
        />

        {/* Notification Badge */}
        {unreadCount > 0 && (
          <span
            className={`
              absolute -top-1.5 -right-1.5
              min-w-[18px] h-[18px] px-1
              bg-red-500 text-white text-[10px] font-bold
              rounded-full
              flex items-center justify-center
              shadow-lg
              transition-transform duration-200
              ${isPulsing ? 'scale-125 animate-pulse' : 'scale-100'}
            `}
          >
            {getBadgeText()}
          </span>
        )}
      </button>

      {/* Profile Quick Bubble */}
      <ProfileQuickBubble
        isOpen={isBubbleOpen}
        onClose={handleCloseBubble}
        profile={profile}
        notifications={notifications}
        unreadCount={unreadCount}
        onMarkAsRead={onMarkAsRead}
        onMarkAllAsRead={onMarkAllAsRead}
        onOpenFullProfile={handleOpenFullProfile}
        onOpenSettings={onOpenSettings}
        onUpdateStatus={onUpdateStatus}
        anchorRef={dockRef}
      />
    </div>
  )
}
